import React, { useRef, useMemo, useState, useEffect } from "react";
import { renderToString } from "react-dom/server";
// Bryntum v7 CSS
import "../gantt/bryntum-common.css";
import "@bryntum/taskboard-thin/taskboard.css";
import "@bryntum/taskboard-thin/stockholm-light.css";
import "./index.css";
import type { BryntumTaskBoardProps } from "@bryntum/taskboard-react-thin";
import { BryntumTaskBoard } from "@bryntum/taskboard-react-thin";
import { useGroupContext } from "@/react/providers/group-context-provider";
import BoardsControlPanel from "./toolbar";
import { loadBoardsSelectedDate } from "./toolbar/date-selector";
import { authManager } from "@/lib/managers/auth-manager";
import type { BryntumFn } from "./event-handlers";
import {
   renewSession,
   onTaskDblClick,
   onBeforeTaskDrop,
   handleTaskClick,
   removeSelectedRequestStyles,
   onTaskMenuOnProcessItems,
   onTaskDrop,
} from "./event-handlers";
import { setupExternalDropHandler } from "./external-drop-handler";
import { getAttachedDate, getDetachedDay } from "@laborchart-modules/common/dist/datetime/date";
import type { TaskMenuItem } from "@bryntum/taskboard-thin";
import { TaskModel } from "@bryntum/taskboard-thin";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import {
   Spinner,
   useI18nContext,
   EmptyState,
   ToolLandingPage,
   SplitViewCard,
   Pagination,
} from "@procore/core-react";
import TearsheetProvider from "../tearsheets";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import configurePanelConfig, { getBoardsConfigurePanelValues } from "./configure-panel";
import type { GanttTearsheetRef } from "../gantt/gantt-tearsheets";
import {
   GanttCreateAssignmentTearsheet,
   GanttCreateRequestTearsheet,
   GanttEditRequestTearsheet,
   GanttEditAssignmentTearsheet,
   GanttPersonDetailsTearsheet,
   GanttSendOneOffAssignmentAlertsTearsheet,
   GanttProjectDetailsTearsheet,
   GanttSendAssignmentAlertsTearsheet,
   GanttTransferAssignmentsTearsheet,
} from "../gantt/gantt-tearsheets";
import { formatMilitaryTimeValue } from "@laborchart-modules/common/dist/datetime";
import ColumnToolbar from "./column-toolbar";
import { TaskCardBodyContent, TaskCardHeaderContent } from "./task-card-content";
import BoardsColumnHeader from "./column-header";
import {
   maybeUpdateColumnWidths,
   tearsheetEquipmentRequestToTask,
   tearsheetRequestToTask,
   transformFiltersForBackend,
   sortTasksByJobTitleSequence,
} from "./utils";
import {
   BoardsFilterPanel,
   getBoardsFilterLocalStorage,
   getClearedBoardsFilter,
   updateBoardsFilterLocalStorage,
   type BoardsFilterTypes,
} from "./filter-panel";
import { BenchPanel } from "../bench/bench-panel";
import { publish, subscribe } from "./hooks/boards-event-bus";
import { useColumnWidthTracker } from "./hooks/use-column-width-tracker";
import { useDynamicTasksPerRow } from "./hooks/use-dynamic-tasks-per-row";
import { ProcoreStore } from "@/stores/procore-store.core";
import { BoardsTaskType } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-boards";

const BOARDS_PAGE_SIZE = 50;

class BoardsTaskModel extends TaskModel {
   static get fields() {
      return [
         { name: "id", type: "string" },
         { name: "name", type: "string" },
         { name: "type", type: "string", alwaysWrite: true },
         { name: "project_id", type: "string", alwaysWrite: true },
         { name: "project_name", type: "string" },
         { name: "resource_id", type: "string" },
         { name: "request_id", type: "string" },
         { name: "creator_id", type: "string" },
         { name: "start_day", type: "number" },
         { name: "end_day", type: "number" },
         { name: "start_time", type: "string" },
         { name: "percent_allocated", type: "number" },
         { name: "end_time", type: "string" },
         { name: "instruction_text", type: "string" },
         { name: "work_scope_text", type: "string" },
         { name: "work_days", type: "object" },
         { name: "job_title_name", type: "string" },
         { name: "job_title_color", type: "string" },
         { name: "job_title_sequence", type: "number" },
         { name: "parent_id", type: "string" },
         { name: "category_id", type: "string", nullable: true },
         { name: "subcategory_id", type: "string", nullable: true },
         { name: "sequence", type: "number" },
         { name: "depth", type: "number" },
         { name: "eventColor", calculate: (r: any) => r.job_title_color ?? "#000000" },
      ];
   }
}

export default function BoardsContainer() {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const taskBoardRef = useRef<BryntumTaskBoard>(null);
   const [isBenchPanelOpen, setIsBenchPanelOpen] = useState(false);
   const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
   const [selectedRequest, setSelectedRequest] = useState<any>(null);
   const selectedRequestRef = useRef<any>(null);
   const [totalPossible, setTotalPossible] = useState(0);
   // Manage filter state in the parent component for immediate reactivity
   const [boardsFilter, setBoardsFilter] = useState<BoardsFilterTypes>(
      getBoardsFilterLocalStorage(),
   );
   const [currentPage, setCurrentPage] = useState(0);
   (window as any).taskBoardRef = taskBoardRef;
   const loadRetryCount = useRef(0);
   const syncRetryCount = useRef(0);
   const projectDetailsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const personDetailsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const assignmentDetailsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const createAssignmentTearsheetRef = useRef<GanttTearsheetRef>(null);
   const createRequestTearsheetRef = useRef<GanttTearsheetRef>(null);
   const editRequestTearsheetRef = useRef<GanttTearsheetRef>(null);
   const sendOneOffAssignmentAlertsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const sendAssignmentAlertsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const transferAssignmentsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const firstTimeLoadingColumnWidths = useRef(true);
   const isDraggingRef = useRef(false);

   // Track columnWidth from localStorage to trigger taskboardConfig memo updates
   const columnWidth = useColumnWidthTracker();

   // Add loading and empty state management
   const [isLoading, setIsLoading] = useState(true);

   useDynamicTasksPerRow(taskBoardRef, isLoading, isDraggingRef, columnWidth);
   const [hasColumns, setHasColumns] = useState(true);

   const { checkAuthAction } = usePermissionContext();
   const canViewProject = checkAuthAction(AuthAction.VIEW_PROJECT);
   const canManageAssignments = checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS);
   const canManageRequests = checkAuthAction(AuthAction.MANAGE_REQUESTS);
   const canManageAssignmentAlerts = checkAuthAction(AuthAction.MANAGE_ALERTS);

   const today = useMemo(() => {
      return getDetachedDay(loadBoardsSelectedDate());
   }, []);
   const [selectedDay, setSelectedDay] = useState<number>(today);

   // Using a memo here so the config panel react isn't re-rendered a bunch, causing Bryntum to freak out.
   const configurePanelStrips = useMemo(() => ({ right: configurePanelConfig(I18n) }), [I18n]);

   // Function to update filters that handles both localStorage and React state
   const updateFilter = <K extends keyof BoardsFilterTypes>(
      key: K,
      value: BoardsFilterTypes[K],
   ) => {
      setCurrentPage(0);
      updateBoardsFilterLocalStorage(key, value);
      setBoardsFilter((prev) => ({ ...prev, [key]: value }));
   };

   // Function to clear all filters
   const clearAllFilters = () => {
      localStorage.removeItem("boards-filter");
      setBoardsFilter(getClearedBoardsFilter());
   };

   useEffect(() => {
      selectedRequestRef.current = selectedRequest;
   }, [selectedRequest]);

   const previousSelectedRequestIdRef = useRef<string | null>(null);
   useEffect(() => {
      if (selectedRequest === null && previousSelectedRequestIdRef.current) {
         removeSelectedRequestStyles(
            taskBoardRef.current?.element as any,
            previousSelectedRequestIdRef.current,
         );
      }
      previousSelectedRequestIdRef.current = selectedRequest?.id ?? null;
   }, [selectedRequest]);

   const taskboardConfig: BryntumTaskBoardProps = useMemo(
      () => ({
         virtualize: false,
         height: "100%",
         stretchCards: false,
         // Using the procore spinner instead of the bryntum one
         loadMask: null,
         syncMask: null,
         errorMask: null,
         project: {
            transport: {
               load: {
                  url: Assignment2Store.getFullRequestUrl({ path: "/api/v3/boards/load" }),
                  method: "GET",
                  // Bryntum v7 dropped credentials: "include" as a default.
                  // Production is cross-origin, so we must include it to send auth cookies.
                  credentials: "include",
                  params: {
                     day_filter: selectedDay,
                     page: currentPage,
                     page_size: BOARDS_PAGE_SIZE,
                     ...(groupId === "my-groups" ? {} : { group_id: groupId }),
                     ...(transformFiltersForBackend(boardsFilter)
                        ? { filters: JSON.stringify(transformFiltersForBackend(boardsFilter)) }
                        : {}),
                  },
               },
               sync: {
                  url: Assignment2Store.getFullRequestUrl({ path: "/api/v3/boards/sync" }),
                  credentials: "include",
               },
            },
            autoLoad: true,
            autoSync: true,
            onLoadFail: ({ response, source }) =>
               renewSession((response as { code: string }).code, loadRetryCount, () =>
                  source.load(),
               ),
            onLoad: ({ response }) => {
               (taskBoardRef.current?.instance as any).company = (response as any).company;
               loadRetryCount.current = 0;
               sortTasksByJobTitleSequence(taskBoardRef);
               setIsLoading(false);
               setTotalPossible((response as any).total_possible);

               // Update selectedDay when taskboard loads with new date and check columns
               // The day_filter is passed as a parameter in the load request
               const taskBoard = taskBoardRef.current?.instance;
               if (taskBoard) {
                  // Check if there are columns (projects) to show
                  const columnsCount = taskBoard.columns?.getCount({ all: true }) || 0;
                  setHasColumns(columnsCount > 0);

                  try {
                     const newSelectedDay = (taskBoard.project as any)?.transport?.load?.params
                        ?.day_filter;
                     if (newSelectedDay && newSelectedDay !== selectedDay) {
                        setSelectedDay(newSelectedDay);
                     }
                  } catch (error) {
                     // Fallback - if we can't read the date from taskboard, keep current selectedDay
                     console.warn("Could not read selected day from taskboard:", error);
                  }
               }
            },
            onSyncFail: ({ response, source }) =>
               renewSession((response as { code: string }).code, syncRetryCount, () =>
                  source.sync(),
               ),
            onSync: () => {
               syncRetryCount.current = 0;
               // Clear changes after sync to ensure tasks are properly marked as persisted
               (taskBoardRef.current?.instance as any).project.acceptChanges();
            },
            onBeforeSend: async ({ requestConfig }: any) => {
               if (requestConfig.method !== "POST") return Promise.resolve();
               // When doing a SYNC request (POST), we need to add the type of the removed task to the request body
               // Despite havinng alwaysWrite: true in the TaskModel, the type is not passed in the request body
               // when tasks are removed.
               const taskStore = taskBoardRef.current?.instance?.project?.taskStore as any;
               const requestBody = JSON.parse(requestConfig.body);
               if (requestBody.tasks?.removed && taskStore) {
                  requestBody.tasks.removed = requestBody.tasks.removed.map(
                     (removed: { id: string }) => ({
                        ...removed,
                        type: taskStore.changes.removed
                           .find((task: any) => task.getData("id") === removed.id)
                           ?.getData("type"),
                     }),
                  );
                  requestConfig.body = JSON.stringify(requestBody);
               }
               return Promise.resolve();
            },
            taskModelClass: BoardsTaskModel,
            taskStore: {
               // No automatic sorter - we manually sort after transfers
            },
         },
         onRenderTasks() {
            if (firstTimeLoadingColumnWidths.current) {
               maybeUpdateColumnWidths();
               firstTimeLoadingColumnWidths.current = false;
            }
         },
         showCollapseInHeader: false,
         showCountInHeader: false,
         onTaskDblClick: (event) =>
            onTaskDblClick({
               ...event,
               assignmentDetailsTearsheetRef,
               editRequestTearsheetRef,
               checkAuthAction,
               currentUserId: authManager.authedUserId(),
               taskBoardRef,
            }),
         onTaskDragStart: () => {
            isDraggingRef.current = true;
            ProcoreStore.logAnalyticsEvent({
               key: "company.workforce_planning.boards.drag_and_drop_initiated",
               properties: {},
            });
         },
         onTaskDragAbort: () => {
            isDraggingRef.current = false;
            ProcoreStore.logAnalyticsEvent({
               key: "company.workforce_planning.boards.drag_and_drop_cancelled",
               properties: {},
            });
         },
         onBeforeTaskEdit: () => false, // Disable default task edit selecting with keyboard.
         onActivateTask: () => false, // Disable left-click tooltip - now handled by right-click
         onTaskClick: (params: Parameters<BryntumFn<"onTaskClick">>[0]) =>
            handleTaskClick(
               params,
               selectedRequestRef,
               setSelectedRequest,
               setIsBenchPanelOpen,
               taskBoardRef,
            ),
         onBeforeTaskDrag: ({ taskRecords }) => {
            // Prevent dragging category tasks
            return !taskRecords.some((task) => task.getData("type") === BoardsTaskType.CATEGORY);
         },
         onBeforeTaskDrop: (event) =>
            onBeforeTaskDrop({
               ...event,
            }),
         onTaskDrop: (event) => {
            isDraggingRef.current = false;
            onTaskDrop({
               ...event,
               assignmentDetailsTearsheetRef,
               transferAssignmentsTearsheetRef,
               sendAssignmentAlertsTearsheetRef,
               taskBoardRef,
               I18n,
               tbdWeeks: (taskBoardRef.current?.instance as any).company.tbd_weeks,
            });
            const assignments = event.taskRecords.filter(
               (task: any) => task.getData("type") === BoardsTaskType.ASSIGNMENT,
            );
            const requests = event.taskRecords.filter(
               (task: any) => task.getData("type") === BoardsTaskType.REQUEST,
            );
            ProcoreStore.logAnalyticsEvent({
               key: "company.workforce_planning.boards.drag_and_drop_completed",
               properties: {
                  scheduled_option: localStorage.getItem("boards-schedule-changes"),
                  assignment_count: assignments.length,
                  request_count: requests.length,
                  is_smart_bench: false,
                  job_title_match: null,
                  tag_match_ratio: null,
                  proximity: null,
               },
            });
            void assignments;
            void requests;
         },
         taskDragFeature: canManageAssignments,
         taskMenuFeature: {
            items: {
               // Default items
               removeTask: null,
               resources: null,
               column: null,
               swimlane: null,
               editTask: null,
               // Labor Assignment Items
               viewPerson: null,
               endAssignment: null,
               deleteAssignment: null,
            },
            processItems: ({ items }: { items: Record<string, TaskMenuItem> }) => {
               return onTaskMenuOnProcessItems({
                  items,
                  taskBoardRef,
                  selectedDay,
                  I18n,
                  personDetailsTearsheetRef,
                  sendAssignmentAlertsTearsheetRef,
                  checkAuthAction,
               });
            },
         },
         newTaskDefaults: {
            weight: 99,
         },
         columnField: "project_id",
         columnTitleRenderer: ({ columnRecord }: any) => ({
            html: renderToString(<BoardsColumnHeader columnRecord={columnRecord} />),
         }),
         columnHeaderMenuFeature: {
            items: {
               addTask: null,
               ...(canViewProject
                  ? {
                       viewProjectDetails: {
                          text: I18n.t(
                             "views.company.workforce_planning.boards.view_project_details",
                          ),
                          onItem: ({ columnRecord }) =>
                             projectDetailsTearsheetRef.current?.handleOpenTearsheet({
                                projectId: columnRecord?.id as string,
                                callback: (response) => {
                                   // A response may be undefined if the project was not edited
                                   // or the tearsheet was closed using the x button
                                   if (!response) return;

                                   const updatedProject = response.updatedProject;
                                   const column = taskBoardRef.current?.instance.columns.getById(
                                      updatedProject.id,
                                   );
                                   if (!column) {
                                      return;
                                   }
                                   const formattedStartTime = formatMilitaryTimeValue(
                                      updatedProject.daily_start_time,
                                   );
                                   const formattedEndTime = formatMilitaryTimeValue(
                                      updatedProject.daily_end_time,
                                   );

                                   column.set("color", updatedProject.color, true);
                                   column.set("daily_end_time", formattedEndTime, true);
                                   column.set("daily_start_time", formattedStartTime, true);
                                   column.set("job_number", updatedProject.job_number, true);
                                   column.set("text", updatedProject.name, true);

                                   if (updatedProject.est_end_date) {
                                      const detachedEndDate = getDetachedDay(
                                         new Date(updatedProject.est_end_date),
                                      );
                                      column.set("est_end_date", detachedEndDate, true);
                                   }

                                   const origData = (column as any).originalData;
                                   if (origData) {
                                      origData.color = updatedProject.color;
                                      origData.daily_end_time = formattedEndTime;
                                      origData.daily_start_time = formattedStartTime;
                                      origData.est_end_date = updatedProject.est_end_date
                                         ? getDetachedDay(new Date(updatedProject.est_end_date))
                                         : null;
                                      origData.start_date =
                                         updatedProject.start_date != null
                                            ? getDetachedDay(new Date(updatedProject.start_date))
                                            : origData.start_date;
                                      origData.job_number = updatedProject.job_number;
                                      origData.name = updatedProject.name;
                                      origData.bid_rate = updatedProject.bid_rate;
                                   }

                                   publish("project-updated", {
                                      projectId: updatedProject.id,
                                   });
                                },
                             }),
                       },
                    }
                  : {}),
               ...(canManageRequests
                  ? {
                       createRequest: {
                          text: I18n.t("views.company.workforce_planning.boards.create_request"),
                          onItem: ({ columnRecord }) => {
                             const selectedBoardsEndDateConfig =
                                localStorage.getItem("boards-schedule-changes");
                             const currentDay =
                                (taskBoardRef.current?.instance.project as any)?.transport?.load
                                   ?.params?.day_filter ?? selectedDay;
                             createRequestTearsheetRef.current?.handleOpenTearsheet({
                                projectId: columnRecord?.id as string,
                                selectedDefaultEndDate: selectedBoardsEndDateConfig ?? undefined,
                                startDate: getAttachedDate(currentDay),
                                callback: async ({ newTasks }: any) => {
                                   // istanbul ignore next
                                   newTasks.forEach((t: any) => {
                                      const taskData =
                                         t.type === BoardsTaskType.EQUIPMENT_REQUEST
                                            ? tearsheetEquipmentRequestToTask(t)
                                            : tearsheetRequestToTask(t, taskBoardRef);
                                      taskBoardRef.current?.instance.addTask(
                                         columnRecord as any,
                                         undefined,
                                         taskData,
                                      );
                                   });
                                   publish("column-tasks-changed", {
                                      projectId: columnRecord?.id as string,
                                   });
                                },
                             });
                          },
                       },
                    }
                  : {}),
               ...(canManageAssignmentAlerts
                  ? {
                       sendAssignmentAlerts: {
                          text: I18n.t(
                             "views.company.workforce_planning.boards.send_assignment_alerts",
                          ),
                          onItem: ({ columnRecord }) => {
                             sendOneOffAssignmentAlertsTearsheetRef.current?.handleOpenTearsheet({
                                projectId: columnRecord?.id as string,
                             });
                          },
                       },
                    }
                  : {}),
               // TODO: Add message people back when ready to implement
               // messagePeople: {
               //    text: I18n.t("views.company.workforce_planning.boards.message_people"),
               // },
            },
         },
         columnToolbarsFeature: {
            topItems: {
               projectInfoTop: {},
            },
            bottomItems: {
               projectInfoBottom: {},
               addTask: false,
            },
            processItems({ items, location, columnRecord }) {
               if (location === "top") {
                  items.projectInfoTop = {
                     type: "widget",
                     html: (
                        <ColumnToolbar
                           columnRecord={columnRecord}
                           location="top"
                           taskBoardRef={taskBoardRef}
                        />
                     ),
                     padding: 0,
                     flex: "0 0 auto",
                  };
               }

               if (location === "bottom") {
                  items.projectInfoBottom = {
                     type: "widget",
                     html: (
                        <ColumnToolbar
                           columnRecord={columnRecord}
                           location="bottom"
                           taskBoardRef={taskBoardRef}
                        />
                     ),
                     padding: 0,
                     flex: "0 0 auto",
                  };
               }
            },
         },
         strips: configurePanelStrips,
         // Task Card Items
         headerItems: {
            text: {
               type: "jsx",
               jsx: ({ taskRecord }: { taskRecord: any }) => {
                  let assignmentCount: number | undefined;
                  if (taskRecord.getData("type") === BoardsTaskType.CATEGORY) {
                     assignmentCount = taskBoardRef.current?.instance.tasks.filter(
                        (task: any) => task.category_id === taskRecord.id,
                     ).length;
                  }
                  return (
                     <TaskCardHeaderContent
                        taskRecord={taskRecord}
                        assignmentCount={assignmentCount ?? undefined}
                     />
                  );
               },
            },
         },
         bodyItems: {
            id: {
               type: "jsx",
               jsx: ({ taskRecord }: { taskRecord: any }) => {
                  if (taskRecord.getData("type") === BoardsTaskType.CATEGORY) {
                     return undefined;
                  }
                  return (
                     <TaskCardBodyContent taskRecord={taskRecord} taskBoardRef={taskBoardRef} />
                  );
               },
            },
         },
         // footerItems: {}
         // End Task Card Items
         taskRenderer({ taskRecord, cardConfig }) {
            const isCategory = taskRecord.getData("type") === BoardsTaskType.CATEGORY;
            const depth = taskRecord.getData("depth") ?? 0;

            // Build class list with depth class for CSS-based indentation
            const classes = cardConfig.cls ? [cardConfig.cls] : [];

            if (isCategory) {
               classes.push("category-task-card");
               cardConfig.style = "border-left: none;";
            }

            // Add depth class for CSS-based margin (more reliable with virtualization)
            if (depth > 0) {
               classes.push(`task-depth-${depth}`);
            }

            cardConfig.cls = classes.join(" ");
         },
         dropOnColumnHeader: true,
         enableDropZone: true,
         features: {
            columnDrag: true,
            taskDrag: true,
         },
      }),
      // Note: I18n and configurePanelStrips intentionally excluded from dependencies
      // to prevent duplicate load calls when translations load asynchronously.
      // These only affect UI rendering, not the data load URL.
      [
         groupId,
         canManageAssignments,
         canViewProject,
         canManageRequests,
         selectedDay,
         boardsFilter,
         currentPage,
      ],
   );
   // Reset loading state when parameters change
   useEffect(() => {
      setCurrentPage(0);
      setIsLoading(true);
      setHasColumns(true);
   }, [groupId, selectedDay, boardsFilter]);

   useEffect(() => {
      // Don't setup the external drop handler if the taskboard is not loaded
      if (isLoading || !taskBoardRef.current?.instance) return;

      return setupExternalDropHandler({
         taskBoardRef,
         createAssignmentTearsheetRef,
      });
   }, [taskBoardRef, isLoading]);

   // Update the column header when the project is updated
   useEffect(() => {
      return subscribe("project-updated", ({ projectId }) => {
         const column = taskBoardRef.current?.instance?.columns?.getById(projectId);
         if (!column) return;
         const columnEl = taskBoardRef.current?.element?.querySelector(
            `[data-column="${projectId}"]`,
         );
         const titleEl = columnEl?.querySelector(".b-task-board-column-title");
         if (titleEl) {
            titleEl.innerHTML = renderToString(<BoardsColumnHeader columnRecord={column} />);
         }
      });
   }, []);

   return (
      <TearsheetProvider>
         <ToolLandingPage className="bryntum-board-border-box">
            <ToolLandingPage.Main>
               <ToolLandingPage.Body>
                  <SplitViewCard className="boards-split-view-card">
                     <SplitViewCard.Main style={{ overflow: "hidden" }}>
                        <SplitViewCard.Section>
                           <Spinner
                              loading={isLoading}
                              label={`${I18n.t("views.company.workforce_planning.loading")}...`}
                              style={{ height: "100%", overflow: "hidden" }}
                           >
                              <div
                                 style={{
                                    height: "100%",
                                    boxSizing: "border-box",
                                    display: "grid",
                                    gridTemplateRows: "auto 1fr",
                                    gap: "0.25em",
                                    position: "relative",
                                    overflow: "hidden",
                                    // paddingRight/paddingLeft changes trigger their respective transitions
                                    transition:
                                       "padding-right 0.3s ease-in-out, padding-left 0.3s ease-in-out",
                                    paddingRight: isBenchPanelOpen ? "390px" : "0",
                                    paddingLeft: isFilterPanelOpen ? "520px" : "0",
                                 }}
                              >
                                 <BoardsControlPanel
                                    taskBoardRef={taskBoardRef}
                                    isBenchPanelOpen={isBenchPanelOpen}
                                    isFilterPanelOpen={isFilterPanelOpen}
                                    onFilterPanelToggle={() =>
                                       setIsFilterPanelOpen(!isFilterPanelOpen)
                                    }
                                    onBenchPanelToggle={() => {
                                       // Toolbar bench toggle should always open the normal bench panel.
                                       setSelectedRequest(null);
                                       setIsBenchPanelOpen(!isBenchPanelOpen);
                                    }}
                                    boardsFilter={boardsFilter}
                                    onFilterUpdate={updateFilter}
                                 />
                                 <div
                                    style={{
                                       position: "relative",
                                       minWidth: 0,
                                       minHeight: 0,
                                       display: "flex",
                                       flexDirection: "column",
                                       overflow: "hidden",
                                    }}
                                 >
                                    {hasColumns ? (
                                       <>
                                          <BryntumTaskBoard
                                             ref={taskBoardRef}
                                             {...taskboardConfig}
                                          />
                                          <Pagination
                                             activePage={currentPage + 1}
                                             perPage={BOARDS_PAGE_SIZE}
                                             onSelectPage={(page) => setCurrentPage(page - 1)}
                                             items={totalPossible}
                                             style={{ justifyContent: "flex-end", flexShrink: 0 }}
                                          />
                                       </>
                                    ) : (
                                       <EmptyState>
                                          <EmptyState.NoResults />
                                          <EmptyState.Title>
                                             {I18n.t(
                                                "views.company.workforce_planning.no_items_match_your_search",
                                             )}
                                          </EmptyState.Title>
                                          <EmptyState.Description>
                                             {I18n.t(
                                                "views.company.workforce_planning.check_your_filter_options",
                                             )}
                                          </EmptyState.Description>
                                       </EmptyState>
                                    )}
                                 </div>
                                 <BoardsFilterPanel
                                    I18n={I18n}
                                    isOpen={isFilterPanelOpen}
                                    onClose={() => setIsFilterPanelOpen(false)}
                                    boardsFilter={boardsFilter}
                                    onFilterUpdate={updateFilter}
                                    onClearAllFilters={clearAllFilters}
                                 />
                                 <BenchPanel
                                    isOpen={isBenchPanelOpen}
                                    onClose={() => {
                                       setSelectedRequest(null);
                                       setIsBenchPanelOpen(false);
                                    }}
                                    onResetSmartBench={() => {
                                       setSelectedRequest(null);
                                    }}
                                    showJobTitles={getBoardsConfigurePanelValues().showJobTitle}
                                    showTags={getBoardsConfigurePanelValues().showTags}
                                    selectedDay={selectedDay}
                                    selectedRequest={selectedRequest}
                                 />
                              </div>
                           </Spinner>
                           <GanttProjectDetailsTearsheet ref={projectDetailsTearsheetRef} />
                           <GanttPersonDetailsTearsheet ref={personDetailsTearsheetRef} />
                           <GanttEditAssignmentTearsheet ref={assignmentDetailsTearsheetRef} />
                           <GanttCreateAssignmentTearsheet ref={createAssignmentTearsheetRef} />
                           <GanttCreateRequestTearsheet ref={createRequestTearsheetRef} />
                           <GanttEditRequestTearsheet ref={editRequestTearsheetRef} />
                           <GanttSendOneOffAssignmentAlertsTearsheet
                              ref={sendOneOffAssignmentAlertsTearsheetRef}
                           />
                           <GanttSendAssignmentAlertsTearsheet
                              ref={sendAssignmentAlertsTearsheetRef}
                           />
                           <GanttTransferAssignmentsTearsheet
                              ref={transferAssignmentsTearsheetRef}
                           />
                        </SplitViewCard.Section>
                     </SplitViewCard.Main>
                  </SplitViewCard>
               </ToolLandingPage.Body>
            </ToolLandingPage.Main>
         </ToolLandingPage>
      </TearsheetProvider>
   );
}
